import classNames from 'classnames';
import { getHomeHref as getHref } from '@/common/SearchBar/utils';
import { PrimaryButton } from '~/components/common/Button';
import { useNativeOpenSoftPullOnClick } from '~/native/home/hooks';
import {
  DynamicPromoCard,
  NewUserPromoCard,
  Header,
  Image,
  MainContentWrapper,
  SubTitle,
  Title
} from './DynamicPromoTile.styles';
import DynamicPromoTileSkeleton from '../../SkeletonLoaders/DynamicPromoTileSkeleton/DynamicPromoTileSkeleton';
import {
  getDynamicTileBtnInfo,
  getDynamicTileCopy,
  getDynamicTileMainContent,
  getTileImgSrc,
  getTileTitle,
  TileStatus,
  TileType
} from '../TileUtils';

type OwnProps = {
  isActive?: boolean;
  isExpired?: boolean;
  isLoaded: boolean;
  isPending?: boolean;
  isWarning?: boolean;
  tileType: TileType;
  isNewUser: boolean;
};

export default function DynamicPromoTile({
  isActive = false,
  isExpired = false,
  isLoaded = false,
  isPending = false,
  isWarning = false,
  tileType,
  isNewUser
}: OwnProps) {
  const handleNativePurchaseOnClick = useNativeOpenSoftPullOnClick();

  if (!isLoaded) return <DynamicPromoTileSkeleton />;

  const statuses = { isActive, isExpired, isPending, isWarning };
  const status = Object.keys(statuses).find(key => statuses[key]);
  const tileAttrs = {
    tileType,
    tileStatus: status ? TileStatus[status] : null
  };
  const tileTitle = getTileTitle(tileType);
  const tileCopy = getDynamicTileCopy({ ...tileAttrs });
  const tileMainContent = getDynamicTileMainContent({ ...tileAttrs });
  const {
    btnText,
    btnLink,
    nativePayload = undefined,
    openNativeSoftPullModal = false
  } = getDynamicTileBtnInfo({ ...tileAttrs });
  const imageUrl = getTileImgSrc(tileType);

  return (
    <>
      <DynamicPromoCard
        className={classNames({
          '!hidden m:!flex': isNewUser
        })}
        data-qa={`${tileType} promo tile`}
      >
        <Header>
          <Title>{tileTitle}</Title>
          <SubTitle>{tileCopy}</SubTitle>
        </Header>

        {status ? (
          <MainContentWrapper>{tileMainContent}</MainContentWrapper>
        ) : (
          <Image src={imageUrl} alt={`${tileType} promo tile`} />
        )}
        <a
          href={getHref(btnLink)}
          data-analytics-id="Home feed card"
          data-analytics-attributes={JSON.stringify({
            description: `${tileType} tile cta`,
            isExpired,
            isActive
          })}
          data-qa={`${tileType} promo tile cta`}
          data-native-included={JSON.stringify(!openNativeSoftPullModal)}
          data-native-payload={nativePayload}
          onClick={event => {
            if (!openNativeSoftPullModal) return;
            handleNativePurchaseOnClick(event);
          }}
        >
          <PrimaryButton
            className="min-h-[40px] w-full"
            data-qa={`${tileType} promo tile link`}
          >
            {btnText}
          </PrimaryButton>
        </a>
      </DynamicPromoCard>
      <NewUserPromoCard
        className={classNames({
          '!hidden': !isNewUser
        })}
        data-qa={`${tileType} promo tile`}
      >
        <Image
          src={imageUrl}
          alt={`${tileType} promo tile`}
          className="!h-[124px] !w-[124px]"
        />
        <div className="flex flex-col py-8">
          <Header className="mb-auto max-w-[180px]">
            <Title className="!tl-header-s">{tileTitle}</Title>
            <SubTitle className="!text-xs">{tileCopy}</SubTitle>
          </Header>
          <a
            href={btnLink}
            data-analytics-id="Home feed card"
            className="!font-bold !uppercase !no-underline tl-link-m"
            data-qa={`${tileType} promo tile link`}
            data-native-payload={nativePayload}
          >
            {btnText}
          </a>
        </div>
      </NewUserPromoCard>
    </>
  );
}
